import React, { useEffect, useState } from 'react';
import styles from './Header.module.scss';
import BarcodeScanner from '../shared/BarcodeScanner/BarcodeScanner';
import useComponentVisible from '../../hooks/useComponentVisible';
import Logo from '../shared/Logo/Logo';
import Modal from '../shared/Modal/Modal';
import TicketResult from '../shared/Modal/ModalContent/Ticket/Ticket';
import Navigation from './Navigation/Navigation';
import HeaderInput from './Input/Input';
import Theme from './Theme/Theme';
import { getTranslation } from '../../utils/translation';
import { useDispatch, useSelector } from 'react-redux';
import { setTicketScanned } from '../../store/actions/tickets';
import FullKeyboard from '../shared/VirtualKeyboard/FullKeyboard';
import ChangeLanguage from '../shared/LanguageSelector/LanguageSelector';
import Date from './Date/Date';
import Devices from './Devices/Devices';
import CurrentLimit from './Balance/Balance';
import { depositVoucher } from '../../store/actions/vouchers';
import { BARCODE_STARTING_CHARACTERS } from '../../constants/defaultValues';
import Account from './Account/Account';

const Header = () => {
    const dispatch = useDispatch();

    const { isAuthenticated, cashierInfo } = useSelector(state => state.auth);

    const [barcodeInputProps, setBarcodeInputProps] = useState({ value: '', showKeyboard: false });

    const {
        ref: ticketDetailsModalRef,
        isComponentVisible: isTicketDetailsModalVisible,
        setIsComponentVisible: setIsTicketDetailsModalVisible,
    } = useComponentVisible(false);

    useEffect(() => {
        onBarcodeSubmit(barcodeInputProps.value);
    }, [barcodeInputProps.value]);

    const toggleBarcodeKeyboardVisibility = () => {
        setBarcodeInputProps(props => ({
            ...props,
            showKeyboard: !props.showKeyboard,
        }));
    };

    const closeBarcodeKeyboard = () => {
        updateBarcodeInputProps({ showKeyboard: false });
    };

    const updateBarcodeInputProps = newProps => {
        setBarcodeInputProps(props => ({
            ...props,
            ...newProps,
        }));
    };

    const onScanError = error => {
        console.error(error);
    };

    const onScan = data => {
        if (data === barcodeInputProps.value) return;

        setIsTicketDetailsModalVisible(false);

        const formattedValue = data.toLowerCase();

        if (
            !formattedValue.startsWith(BARCODE_STARTING_CHARACTERS.coupon) ||
            !formattedValue.startsWith(BARCODE_STARTING_CHARACTERS.voucher)
        ) {
            dispatch(setTicketScanned(true));
        }

        updateBarcodeInputProps({ value: data });
    };

    const handleVoucherScan = async voucherCode => {
        const response = await dispatch(depositVoucher({ voucherCode: voucherCode.substring(1) }));

        if (response.info.success) {
            updateBarcodeInputProps({ value: '' });
        }
    };

    const handleTicketScan = () => {
        setIsTicketDetailsModalVisible(true);
    };

    const onBarcodeSubmit = value => {
        const formattedValue = value.toLowerCase();

        if (formattedValue.startsWith(BARCODE_STARTING_CHARACTERS.voucher)) {
            if (formattedValue.length === 14) {
                handleVoucherScan(formattedValue);
            }
        } else {
            if (formattedValue.length === 13) {
                closeBarcodeKeyboard();
                handleTicketScan();
            }
        }
    };

    const onModalClose = () => {
        setIsTicketDetailsModalVisible(false);
    };

    return (
        <>
            {isTicketDetailsModalVisible && (
                <Modal
                    ref={ticketDetailsModalRef}
                    isVisible={isTicketDetailsModalVisible}
                    width='fit-content'
                    minWidth={'900px'}
                    maxWidth={'90vw'}
                    contentPadding='50px'
                    overflow={'auto'}
                >
                    <TicketResult
                        resetBarcodeKeyboard={() => updateBarcodeInputProps({ value: '' })}
                        ticketCode={barcodeInputProps.value}
                        setVisibleModal={setIsTicketDetailsModalVisible}
                        closeModal={onModalClose}
                    />
                </Modal>
            )}
            {barcodeInputProps.showKeyboard && (
                <FullKeyboard
                    inputValue={barcodeInputProps.value}
                    setInputValue={value => updateBarcodeInputProps({ value })}
                    close={toggleBarcodeKeyboardVisibility}
                    style={{ top: '120px', right: '120px' }}
                />
            )}
            <div className={styles.header}>
                <div className={'flex align-center'}>
                    <Logo className={'m-r-12'} />
                    {isAuthenticated && <Navigation />}
                </div>
                {isAuthenticated && cashierInfo.currentBalance > 0 && (
                    <CurrentLimit limit={cashierInfo.currentBalance} />
                )}
                {isAuthenticated && <Account terminalCode={cashierInfo.terminalCode} />}
                <div className={'white-space-nowrap'}>
                    <Date />
                </div>

                <div className={'flex align-center'}>
                    {isAuthenticated && (
                        <div className={'m-r-16'}>
                            <HeaderInput
                                disabled={barcodeInputProps.value === ''}
                                width='380px'
                                maxLength={14}
                                buttonText={getTranslation('common.actions.checkIt')}
                                placeholder={getTranslation('input.placeholder.scanBarcode')}
                                onSubmit={() => onBarcodeSubmit(barcodeInputProps.value)}
                                value={barcodeInputProps.value}
                                onChange={e => updateBarcodeInputProps({ value: e.target.value })}
                                onFocus={e => e.target.select()}
                                showVirtualKeyboard={toggleBarcodeKeyboardVisibility}
                            />
                            <BarcodeScanner onError={onScanError} onScan={onScan} />
                        </div>
                    )}
                    <div className={styles.settingsArea}>
                        {isAuthenticated && <Devices />}
                        <Theme />
                        <div className={'w-40 flex align-center'}>
                            <ChangeLanguage />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Header;
